/**
 * The pane's controls: which vendor the pane is showing (in the heading), the year it is
 * scoped to and the sort order (at the right of the title row), and the page size (in the
 * footer).
 *
 * All of them are one component — `Dropdown` — in two variants. That is where the keyboard
 * handling, the menu and the styled options live; these three only say what the options are.
 *
 * **The icon lives inside the control, not beside it** (owner), so each pill reads as one
 * object: glyph, value, chevron. A label in front of a select costs more width than it earns
 * when the value already says what it is — "2024" and "Newest first" are not ambiguous — and
 * the row already holds the heading plus two controls. The words survive as the accessible
 * name and the tooltip.
 *
 * The version control no longer chooses anything — it reports the year of the instance
 * picked in the instance card, read-only. See `VersionScope`.
 */
import { useLocale } from "../../i18n/LocaleProvider";
import { DEFAULT_SKILL_SORT, PAGE_SIZE_OPTIONS, type SkillSort } from "../../lib/skills";
import { Dropdown, type DropdownOption } from "./Dropdown";
import { SortIcon } from "./icons";

/**
 * Empty string means every year — not a year anyone has, so it needs no sentinel.
 *
 * Still the scope's resting value, even though nothing offers it as a choice any more: it is
 * what `atVersion` and the organization fetch read as "do not narrow", and it is what the
 * pane holds for the moment before an instance's year is known.
 */
export const ALL_VERSIONS = "";

/**
 * Which product year the pane is scoped to — **shown here, chosen in the instance card.**
 *
 * This was a filter: every year the vendor holds locally plus every year the organization
 * had shared, freely selectable, with a quiet "open" hint on the ones that had an instance
 * running. It listed years you could not act on — a 2021 store with no Revit 2021 open is a
 * list of skills that cannot run — and it let the pane and the instance card disagree about which
 * instance the user was working with, which is the one thing `selectedInstance.ts` exists to
 * prevent.
 *
 * So the scope now **follows the selection** (owner, 2026-09-20): the instance picked in the
 * instance card decides the year, and the pane shows that year's skills, folders and shared
 * rows. There is exactly one place to change it, and it is the place that already decides
 * which document a skill would run against.
 *
 * It stays a dropdown rather than becoming a label — read-only, `aria-disabled`, listing the
 * years with an instance open — because the year *is* a choice; it is simply made one control
 * down. Rendering it as a pill keeps the shape of the row and the shape of the control for
 * the day it becomes selectable here again.
 *
 * **No filter glyph and no chevron while it is read-only** (owner). Both are claims the
 * control cannot honour: a funnel says "narrow this" and a caret says "opens", and neither is
 * true of a value the instance card sets. What is left is the year in a quiet pill, which is
 * what it actually is. They come back with the behaviour, not before it.
 */
export function VersionScope({
  connected,
  value,
}: {
  /** The years of this vendor with an instance open right now, ascending. */
  connected: readonly string[];
  /** The selected instance's year — what the trigger reads. */
  value: string;
}) {
  const { t } = useLocale();
  // No instance, no scope. The pane shows the "open Revit" state instead, so this never
  // renders as an empty pill.
  if (!value) return null;

  /**
   * Every connected year, with the one in force among them.
   *
   * A year that is running but not selected is still listed: that is what says the control
   * names *a* choice rather than a fact, and it is the set that becomes selectable when the
   * pick moves here. `value` is prepended when it is somehow not among them — an instance
   * whose year the store has not caught up with — so the trigger can never read blank.
   */
  const options: DropdownOption<string>[] = connected.map((v) => ({
    value: v,
    label: v,
    hint: t("skills.versionOpen"),
  }));
  if (!connected.includes(value)) options.unshift({ value, label: value });

  return (
    <Dropdown
      value={value}
      options={options}
      onChange={() => {}}
      disabled
      label={t("skills.versionScopeLabel")}
      variant="pill"
    />
  );
}

/**
 * Two options, and they name a **direction** rather than a key: the version is always the
 * primary sort, ascending, so this orders the skills within each version. Both lists offer
 * the same two, which is why there is no per-list configuration here any more.
 */
const SORTS: readonly SkillSort[] = ["newest", "oldest"];

export function SortSelect({
  value,
  onChange,
}: {
  value: SkillSort;
  onChange: (next: SkillSort) => void;
}) {
  const { t } = useLocale();

  return (
    <Dropdown
      value={SORTS.includes(value) ? value : DEFAULT_SKILL_SORT}
      options={SORTS.map((option) => ({ value: option, label: t(`skills.sort.${option}`) }))}
      onChange={onChange}
      label={t("skills.sortLabel")}
      variant="pill"
      glyph={<SortIcon size={13} />}
    />
  );
}

/**
 * How many cards a page holds — 10, 20 or 50, beside the pager in the footer (owner).
 *
 * It lives down there and not in the head row for two reasons: it is a property of the pager
 * next to it rather than of the list above it, and the head row is already full. The trigger
 * shows the number and the unit ("20 / page") because a bare number in a footer could be
 * anything.
 */
export function PageSizeSelect({
  value,
  onChange,
}: {
  value: number;
  onChange: (next: number) => void;
}) {
  const { t } = useLocale();

  return (
    <Dropdown
      value={String(value)}
      options={PAGE_SIZE_OPTIONS.map((size) => ({
        value: String(size),
        label: t("skills.pageSizeOption", { count: size }),
      }))}
      onChange={(next) => onChange(Number(next))}
      label={t("skills.pageSize")}
      variant="pill"
    />
  );
}
